import { useContext, useId } from "react";
import { track } from "../track";
import type { PriceOffer } from "../types";
import type { Words } from "./copy";
import BaseScene from "./BaseScene";
import { BASE_CHARSET, cleanMessage, messageLength, baseKindOf, takesBase } from "./christmas";
import { PageContext } from "./page";

/** The base a piece stands on, as the page holds it (PageInfo.bases): read
 *  under the first key, written under every key an order sheet or card reads
 *  it by, so the sheet opens on it. Christmas only where the page offers it
 *  (PageInfo.baseLimits); plain otherwise. */
function useBase(leadKind: string | undefined, offer: PriceOffer | null | undefined, keys: string[]) {
  const kind = baseKindOf(leadKind, offer);
  const page = useContext(PageContext);
  const limits = page?.baseLimits ?? null;
  const offered = !!limits && takesBase(kind);
  const choice = page?.bases?.[keys[0] ?? ""];
  const message = choice?.message ?? "";
  return {
    kind,
    offered,
    on: offered && !!choice?.on,
    message,
    clean: cleanMessage(message),
    // The field takes as many as the biggest size does; the sheet checks the
    // size picked (baseBlocks).
    widest: limits
      ? Math.max(0, ...Object.values(limits[kind as keyof typeof limits] ?? {}).map(Number).filter(Number.isFinite))
      : 0,
    set: (next: { on: boolean; message: string }) => keys.forEach((k) => page?.onBase?.(k, next)),
  };
}

/** Their figure on its base, in the hero where the drawing was (Hero `stage`):
 *  a statue drawn on a transparent background is shown only standing on the
 *  base it is printed on, the one picked under it, with its message. */
export function BaseStage({
  kind,
  offer,
  drawingUrl,
  keys,
  words: t,
  onReady,
  onFail,
}: {
  kind: string | undefined;
  offer?: PriceOffer | null;
  drawingUrl: string;
  keys: string[];
  words: Words;
  onReady: () => void;
  onFail: () => void;
}) {
  const b = useBase(kind, offer, keys);
  const placeholder = t.t("basePlaceholder");
  return (
    <BaseScene
      className="hero-stage"
      drawingUrl={drawingUrl}
      style={b.on ? "christmas" : "plain"}
      text={b.on ? b.clean : ""}
      placeholder={placeholder}
      label={b.on ? `${t.t("baseMockNote")} ${b.clean || placeholder}` : t.t("basePlain")}
      fitHeight={0.56}
      onReady={onReady}
      onFail={onFail}
    />
  );
}

/** The base under the drawing, so it is seen where they decide and not only
 *  inside the order sheet: the plain base, and where the page offers the
 *  Christmas base a second chip swaps it in and a field writes their message
 *  on it as they type.
 *
 *  The hero shows their figure on the base (BaseStage), so this is only the
 *  chips and the field, and nothing at all when there is no choice to make. A
 *  drawing made before statues were drawn on a transparent background has no
 *  figure alone to stand on it (`scene`): its hero is the drawing, and the
 *  Christmas base is shown here on its own once they pick it. */
export default function BaseShowcase({
  kind: leadKind,
  offer,
  drawingUrl,
  keys,
  scene = true,
  words: t,
}: {
  kind: string | undefined;
  /** The piece's price, which says whether it is a statue of the animals alone. */
  offer?: PriceOffer | null;
  drawingUrl: string | null;
  /** Where the page keeps this piece's base choice: the first is read, all are written. */
  keys: string[];
  /** The Christmas base drawn here, once picked: for a drawing whose figure
   *  cannot stand on it in the hero. */
  scene?: boolean;
  words: Words;
}) {
  const { kind, offered, on, message, clean, widest, set } = useBase(leadKind, offer, keys);
  const id = `xs${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;
  if (!kind || !takesBase(kind) || !drawingUrl) return null;
  // Nothing to choose, nothing to say: the hero shows the figure on its base,
  // or (a drawing from before) the drawing, and a plain base alone says nothing.
  if (!offered || widest <= 0) return null;

  const bad = on && !BASE_CHARSET.test(clean);
  const placeholder = t.t("basePlaceholder");

  return (
    <div className="xshow">
      <div className="xshow-chips" role="group" aria-label={t.t("baseShowTitle")}>
        <button
          type="button"
          className="xshow-chip"
          aria-pressed={!on}
          onClick={() => {
            track("base.showcase", { on: false, kind });
            set({ on: false, message });
          }}
        >
          {t.t("basePlain")}
        </button>
        <button
          type="button"
          className="xshow-chip"
          aria-pressed={on}
          onClick={() => {
            track("base.showcase", { on: true, kind });
            set({ on: true, message });
          }}
        >
          {t.t("baseToggle")}
        </button>
      </div>
      {on && scene && (
        // A drawing from before has no figure alone, so the Christmas base
        // and its message are shown here on their own, once picked.
        <BaseScene
          drawingUrl={null}
          style="christmas"
          text={clean}
          placeholder={placeholder}
          label={`${t.t("baseMockNote")} ${clean || placeholder}`}
        />
      )}
      {on && (
        <div className="xbase-fields">
          <label className="xbase-label tiny" htmlFor={`${id}-message`}>
            {t.t("baseMessage")}
          </label>
          <input
            id={`${id}-message`}
            className="field xbase-input"
            type="text"
            value={message}
            maxLength={widest * 2}
            placeholder={placeholder}
            autoComplete="off"
            enterKeyHint="done"
            aria-invalid={bad ? true : undefined}
            onChange={(e) => set({ on: true, message: e.target.value })}
          />
          <p className={bad ? "tiny xbase-why" : "tiny muted xbase-count"} aria-live="polite">
            {bad ? t.t("baseCharset") : t.t("baseCount", { n: String(messageLength(message)), max: String(widest) })}
          </p>
        </div>
      )}
      {on && <p className="tiny muted xbase-note">{t.t("baseMockNote")}</p>}
    </div>
  );
}
